import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Trash2, RefreshCw, PenLine } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];

export default function DataManagement() {
  const [vendors, setVendors] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [purgeYear, setPurgeYear] = useState(new Date().getFullYear().toString());
  const [purgeMonth, setPurgeMonth] = useState((new Date().getMonth() + 1).toString());
  const [purging, setPurging] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);

  const [vendorOld, setVendorOld] = useState('');
  const [vendorNew, setVendorNew] = useState('');
  const [customerOld, setCustomerOld] = useState('');
  const [customerNew, setCustomerNew] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [v, c] = await Promise.all([
        axios.get(`${API}/masters/vendors`),
        axios.get(`${API}/masters/customers`),
      ]);
      setVendors(v.data);
      setCustomers(c.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handlePurge = async () => {
    if (!window.confirm(`WARNING: This will permanently delete ALL transaction data for ${MONTHS[parseInt(purgeMonth)-1]} ${purgeYear}.\n\nThis action CANNOT be undone. Are you sure?`)) return;
    if (!window.confirm('FINAL CONFIRMATION: Type YES to proceed with data purge.')) return;
    setPurging(true);
    setPurgeResult(null);
    try {
      const res = await axios.delete(`${API}/admin/purge-month/${purgeYear}/${purgeMonth}`);
      setPurgeResult(res.data);
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  const handleRenameVendor = async () => {
    if (!vendorOld || !vendorNew) { toast.error('Select vendor and enter new code'); return; }
    if (!window.confirm(`Rename vendor "${vendorOld}" to "${vendorNew}" across ALL data?`)) return;
    try {
      const res = await axios.put(`${API}/admin/rename-vendor/${vendorOld}/${vendorNew}`);
      toast.success(res.data.message);
      setVendorOld(''); setVendorNew('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rename failed');
    }
  };

  const handleRenameCustomer = async () => {
    if (!customerOld || !customerNew) { toast.error('Select customer and enter new code'); return; }
    if (!window.confirm(`Rename customer "${customerOld}" to "${customerNew}" across ALL data?`)) return;
    try {
      const res = await axios.put(`${API}/admin/rename-customer/${customerOld}/${customerNew}`);
      toast.success(res.data.message);
      setCustomerOld(''); setCustomerNew('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rename failed');
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({length: 5}, (_, i) => (currentYear - 2 + i).toString());

  return (
    <div className="space-y-8" data-testid="data-management-page">
      <div>
        <h1 className="text-4xl font-bold tracking-tight uppercase" style={{fontFamily: 'Barlow Condensed, sans-serif'}} data-testid="data-management-title">DATA MANAGEMENT</h1>
        <p className="text-sm text-text-muted mt-2">Admin tools for bulk data operations</p>
      </div>

      {/* Purge Month Data */}
      <div className="bg-white border-2 border-red-200 rounded-sm">
        <div className="p-4 border-b border-red-200 bg-red-50/50">
          <div className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-red-600" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-red-800">Purge Monthly Data</h2>
          </div>
          <p className="text-xs text-red-600 mt-1">Permanently delete ALL transaction data for a selected month. Master data (Items, Vendors, Customers) is preserved.</p>
        </div>
        <div className="p-6">
          <div className="flex items-end gap-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Year</Label>
              <Select value={purgeYear} onValueChange={setPurgeYear}>
                <SelectTrigger className="w-28 mt-1.5 rounded-none" data-testid="purge-year">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Month</Label>
              <Select value={purgeMonth} onValueChange={setPurgeMonth}>
                <SelectTrigger className="w-40 mt-1.5 rounded-none" data-testid="purge-month">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((m, i) => <SelectItem key={i+1} value={(i+1).toString()}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handlePurge}
              disabled={purging}
              className="bg-red-600 text-white hover:bg-red-700 rounded-sm font-medium"
              data-testid="purge-button"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {purging ? 'PURGING...' : 'PURGE DATA'}
            </Button>
          </div>
          {purgeResult && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-sm" data-testid="purge-result">
              <p className="text-sm font-semibold text-red-800">{purgeResult.message}</p>
              {Object.entries(purgeResult.deleted || {}).length > 0 ? (
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {Object.entries(purgeResult.deleted).map(([col, count]) => (
                    <div key={col} className="text-xs text-red-600">
                      <span className="font-mono font-semibold">{col}:</span> {count} deleted
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-red-500 mt-1">No records found for this month.</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Rename Vendor Code */}
      <div className="bg-white border border-border rounded-sm">
        <div className="p-4 border-b border-border bg-gray-50/50">
          <div className="flex items-center gap-2">
            <PenLine className="w-5 h-5 text-blue-600" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-gray-800">Rename Vendor Code</h2>
          </div>
          <p className="text-xs text-text-muted mt-1">Change a vendor's ID across the master and all transactions (issues, receipts, challans).</p>
        </div>
        <div className="p-6">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Current Vendor</Label>
              <Select value={vendorOld} onValueChange={setVendorOld}>
                <SelectTrigger className="mt-1.5 rounded-none" data-testid="vendor-old-select">
                  <SelectValue placeholder="Select vendor..." />
                </SelectTrigger>
                <SelectContent>
                  {vendors.map(v => <SelectItem key={v.vendor_id} value={v.vendor_id}>{v.vendor_id} — {v.vendor_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">New Vendor Code</Label>
              <Input
                value={vendorNew}
                onChange={(e) => setVendorNew(e.target.value)}
                placeholder="Enter new vendor code..."
                className="mt-1.5 rounded-none"
                data-testid="vendor-new-input"
              />
            </div>
            <Button onClick={handleRenameVendor} className="bg-blue-600 text-white hover:bg-blue-700 rounded-sm font-medium" data-testid="rename-vendor-btn">
              <RefreshCw className="w-4 h-4 mr-2" />RENAME
            </Button>
          </div>
        </div>
      </div>

      {/* Rename Customer Code */}
      <div className="bg-white border border-border rounded-sm">
        <div className="p-4 border-b border-border bg-gray-50/50">
          <div className="flex items-center gap-2">
            <PenLine className="w-5 h-5 text-indigo-600" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-gray-800">Rename Customer Code</h2>
          </div>
          <p className="text-xs text-text-muted mt-1">Change a customer's ID across the master and all transactions (dispatches, schedules, dispatch plans).</p>
        </div>
        <div className="p-6">
          <div className="flex items-end gap-4">
            <div className="flex-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">Current Customer</Label>
              <Select value={customerOld} onValueChange={setCustomerOld}>
                <SelectTrigger className="mt-1.5 rounded-none" data-testid="customer-old-select">
                  <SelectValue placeholder="Select customer..." />
                </SelectTrigger>
                <SelectContent>
                  {customers.map(c => <SelectItem key={c.customer_id} value={c.customer_id}>{c.customer_id} — {c.customer_name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-gray-700">New Customer Code</Label>
              <Input
                value={customerNew}
                onChange={(e) => setCustomerNew(e.target.value)}
                placeholder="Enter new customer code..."
                className="mt-1.5 rounded-none"
                data-testid="customer-new-input"
              />
            </div>
            <Button onClick={handleRenameCustomer} className="bg-indigo-600 text-white hover:bg-indigo-700 rounded-sm font-medium" data-testid="rename-customer-btn">
              <RefreshCw className="w-4 h-4 mr-2" />RENAME
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
